import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Dialog from '@mui/material/Dialog';
import {
  spacing,
  fontSize,
  mediaMax,
  borderRadius,
} from 'presentations/views/styles';
import { gray03 } from 'presentations/views/styles/color';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';

interface Props {
  isOpen: boolean;
  onClose?: any;
  title?: string;
  titleComponent?: ReactNode;
  titleBottomMargin?: string;
  children?: ReactNode;
  actions?: ReactNode;
  maxWidth?: number;
  isFillWidth?: boolean;
  width?: number;
  className?: string;
  scroll?: boolean;
  disableMinWidth?: boolean;
  contentBorderColor?: any;
  maxHeight?: string;
  minHeight?: string;
  mobileMaxHeight?: string;
  zIndex?: number;
  disableRestoreFocus?: boolean;
  isFullScreen?: boolean;
  transitionComponent?: any;
}

export default function Component(props: Props) {
  return (
    <StyledDialog
      className={props.className}
      maxWidth="lg"
      fullScreen={props.isFullScreen}
      onClose={props.onClose}
      open={props.isOpen}
      scroll="paper"
      PaperProps={{
        style: {
          maxWidth: props.maxWidth,
          width: props.width,
        },
      }}
      zindex={props.zIndex}
      disableRestoreFocus={props.disableRestoreFocus}
      TransitionComponent={props.transitionComponent}
    >
      <Container
        scroll={props.scroll}
        isFillWidth={props.isFillWidth}
        disableMinWidth={props.disableMinWidth}
        maxHeight={props.maxHeight}
        minHeight={props.minHeight}
        mobileMaxHeight={props.mobileMaxHeight}
      >
        {props.titleComponent ? (
          <Title titleBottomMargin={props.titleBottomMargin}>
            {props.titleComponent}
          </Title>
        ) : (
          <Title titleBottomMargin={props.titleBottomMargin}>
            <span>{props.title}</span>
            {props.onClose && (
              <CloseIconButton aria-label="close" onClick={props.onClose}>
                <CloseIcon name="close" size={fontSize(26)} />
              </CloseIconButton>
            )}
          </Title>
        )}
        {props.children && (
          <Content
            scroll={props.scroll}
            contentBorderColor={props.contentBorderColor}
          >
            {props.children}
          </Content>
        )}
        {props.actions && <Action>{props.actions}</Action>}
      </Container>
    </StyledDialog>
  );
}

const StyledDialog = styled(Dialog)<{ zindex?: number }>`
  ${(props) =>
    props.zindex &&
    css`
      z-index: ${props.zindex} !important;
    `}

  .MuiPaper-root.MuiDialog-paper.MuiPaper-rounded {
    border-radius: ${borderRadius(16)};
  }
`;
const Container = styled.div<{
  scroll?: boolean;
  isFillWidth?: boolean;
  disableMinWidth?: boolean;
  maxHeight?: string;
  minHeight?: string;
  mobileMaxHeight?: string;
}>`
  min-width: ${({ disableMinWidth }) =>
    disableMinWidth ? 'initial' : '600px'};
  ${mediaMax.tablet} {
    width: 100%;
    min-width: 100%;
    ${({ mobileMaxHeight }) =>
      mobileMaxHeight &&
      css`
        max-height: ${mobileMaxHeight};
      `}
  }
  ${({ maxHeight }) =>
    maxHeight &&
    css`
      max-height: ${maxHeight};
    `}
  ${({ minHeight }) =>
    minHeight &&
    css`
      min-height: ${minHeight};
    `}
  ${({ isFillWidth }) =>
    isFillWidth &&
    css`
      width: 100%;
      min-width: initial;
    `}
  padding: ${spacing(16)} ${spacing(20)};

  ${({ scroll }) =>
    scroll &&
    css`
      overflow-y: auto;
      display: flex;
      flex-direction: column;
    `};
`;
const Title = styled.h2<{
  titleBottomMargin?: string;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ titleBottomMargin }) =>
    titleBottomMargin ? titleBottomMargin : spacing(16)};
  font-size: ${fontSize(20)};
`;
const CloseIconButton = styled(IconButton)`
  color: ${gray03};
`;
const CloseIcon = styled(Icon)``;
const Content = styled.div<{ scroll?: boolean; contentBorderColor?: any }>`
  ${({ scroll }) =>
    scroll &&
    css`
      overflow-y: auto;
    `};
  ${({ contentBorderColor }) =>
    contentBorderColor &&
    css`
      border-top: 1px solid ${contentBorderColor};
      border-bottom: 1px solid ${contentBorderColor};
    `}
`;
const Action = styled.div`
  margin-top: ${spacing(16)};
`;
